import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  TextInput,
} from 'react-native';
import DropDownPicker from 'react-native-dropdown-picker';
import { DLODropdownOption } from '../../types/dlo.types';
import { DayItem } from '../DLOFilterByDayModal';
import { EmployeeRowData } from './DLOCreateScheduleEmployeeRow';

export interface DLOCreateScheduleSplitRoleRowProps {
  data: EmployeeRowData;
  parentData?: EmployeeRowData; // Parent row data to check shift selection
  onUpdate: (id: string, updatedData: Partial<EmployeeRowData>) => void;
  onDelete?: (id: string) => void;
  roleOptions: DLODropdownOption[];
  shiftOptions?: DLODropdownOption[];
  selectedDays: string[];
  dayFilter?: DayItem[];
  // Global dropdown management props
  isRoleDropdownOpen?: boolean;
  isShiftDropdownOpen?: boolean;
  onDropdownOpen?: (rowId: string, type: 'role' | 'shift', isOpen: boolean) => void;
  // Loading states
  isRoleLoading?: boolean;
  isShiftLoading?: boolean;
  // Checkbox control
  isLastChild?: boolean; // Whether this is the last child split role for its parent
  // Input control
  inputsEnabled?: boolean; // Controls whether inputs are editable based on week cutoff
}

const DLOCreateScheduleSplitRoleRow: React.FC<DLOCreateScheduleSplitRoleRowProps> = ({
  data,
  parentData,
  onUpdate,
  onDelete,
  roleOptions,
  shiftOptions = [],
  selectedDays,
  dayFilter = [],
  isRoleDropdownOpen = false,
  isShiftDropdownOpen = false,
  onDropdownOpen,
  isRoleLoading = false,
  isShiftLoading = false,
  isLastChild = false,
  inputsEnabled = true,
}) => {
  // Determine initial lock state: unlock if parent has no shift, otherwise use data.isLocked or default to true
  const getInitialLockState = () => {
    if (parentData && (!parentData.shift || !parentData.shift.value)) {
      return false; // Unlock if parent has no shift
    }
    return data.isLocked ?? true; // Use existing lock state or default to locked
  };

  const [isShiftLocked, setIsShiftLocked] = useState(getInitialLockState());
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [shiftDropdownOpen, setShiftDropdownOpen] = useState(false);
  const [roleValue, setRoleValue] = useState(data.role?.value || null);
  const [shiftValue, setShiftValue] = useState(data.shift?.value || null);

  // Handle checkbox change - restores normal selection behavior
  const handleCheckboxChange = () => {
    onUpdate(data.id, { isSelected: !data.isSelected });
  };

  // All child row checkboxes are now enabled (removed isLastChild restriction)
  const shouldDisableCheckbox = false;

  // Handle role selection
  const handleRoleSelect = (value: string | null) => {
    if (!inputsEnabled) return;
    setRoleValue(value);
    const selectedRole = roleOptions.find(option => option.value === value) || null;
    onUpdate(data.id, { role: selectedRole, isRoleInvalid: false });
  };

  // Handle shift selection
  const handleShiftSelect = (value: string | null) => {
    if (!inputsEnabled) return;
    setShiftValue(value);
    const selectedShift = shiftOptions.find(option => option.value === value) || null;
    onUpdate(data.id, { shift: selectedShift });
  };

  // Handle hour field changes
  const handleHourChange = (day: string, value: string) => {
    if (!inputsEnabled) return;
    // Validate input - only allow numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    
    // Calculate new total
    const updatedHours = {
      sunday: day === 'sunday' ? numericValue : data.sunday,
      monday: day === 'monday' ? numericValue : data.monday,
      tuesday: day === 'tuesday' ? numericValue : data.tuesday,
      wednesday: day === 'wednesday' ? numericValue : data.wednesday,
      thursday: day === 'thursday' ? numericValue : data.thursday,
      friday: day === 'friday' ? numericValue : data.friday,
      saturday: day === 'saturday' ? numericValue : data.saturday,
    };
    
    const newTotal = Object.values(updatedHours).reduce((sum, hours) => {
      return sum + (parseFloat(hours) || 0);
    }, 0);
    
    onUpdate(data.id, { 
      [day]: numericValue,
      total: newTotal.toString()
    });
  };

  // Validation functions for max hours checking
  
  // Helper function to extract shift data from dropdown value
  const getShiftData = (shift: DLODropdownOption | null): { id: string; shift_type: string; time_format: string; time_value: string } | null => {
    if (!shift || !shift.value) return null;
    
    try {
      // If value is JSON, parse it
      return JSON.parse(shift.value);
    } catch (error) {
      // If parsing fails, assume it's the old format (just ID)
      console.warn('Could not parse shift value as JSON, using fallback:', shift.value);
      return {
        id: shift.id,
        shift_type: '',
        time_format: '',
        time_value: ''
      };
    }
  };
  
  const getShiftMaxHours = (): number => {
    // First check the current row's shift
    const currentShiftData = getShiftData(data.shift);
    if (currentShiftData && currentShiftData.shift_type) {
      const shiftType = currentShiftData.shift_type.toLowerCase();
      if (shiftType.includes('5x8')) return 8;
      if (shiftType.includes('4x10')) return 10;
    }
    
    // Fallback to checking current row's label if shift_type is not available
    const currentShiftLabel = data.shift?.label?.toLowerCase() || '';
    if (currentShiftLabel.includes('5x8')) return 8;
    if (currentShiftLabel.includes('4x10')) return 10;
    
    // If no shift on current row, check parent row's shift
    const parentShiftData = getShiftData(parentData?.shift || null);
    if (parentShiftData && parentShiftData.shift_type) {
      const shiftType = parentShiftData.shift_type.toLowerCase();
      if (shiftType.includes('5x8')) return 8;
      if (shiftType.includes('4x10')) return 10;
    }
    
    // Fallback to checking parent row's label
    const parentShiftLabel = parentData?.shift?.label?.toLowerCase() || '';
    if (parentShiftLabel.includes('5x8')) return 8;
    if (parentShiftLabel.includes('4x10')) return 10;
    
    return 12; // Default max hours per day
  };

  const getDayHours = (day: string): number => {
    const dayValues: { [key: string]: string } = {
      sunday: data.sunday,
      monday: data.monday,
      tuesday: data.tuesday,
      wednesday: data.wednesday,
      thursday: data.thursday,
      friday: data.friday,
      saturday: data.saturday
    };
    return parseFloat(dayValues[day] || '0') || 0;
  };

  const getWeeklyHours = (): number => {
    const weekdayValues = [
      data.sunday,
      data.monday,
      data.tuesday,
      data.wednesday,
      data.thursday,
      data.friday,
      data.saturday
    ];

    return weekdayValues.reduce((sum, value) => {
      const numericValue = parseFloat(value) || 0;
      return sum + numericValue;
    }, 0);
  };

  const isDayHoursExceeded = (day: string): boolean => {
    const dayHours = getDayHours(day);
    const maxHours = getShiftMaxHours();
    return dayHours > maxHours;
  };

  const isDailyMaxExceeded = (day: string): boolean => {
    const dayHours = getDayHours(day);
    return dayHours > 12; // General daily max is 12 hours
  };

  const isWeeklyMaxExceeded = (): boolean => {
    const weeklyHours = getWeeklyHours();
    return weeklyHours > 60; // Weekly max is 60 hours
  };

  const isWeeklyHighlightThreshold = (): boolean => {
    const weeklyHours = getWeeklyHours();
    return weeklyHours > 40; // Highlight after 40 hours
  };

  const getValidationMessage = (day: string): string => {
    if (day === 'total') {
      if (isWeeklyMaxExceeded()) {
        return 'Weekly hours exceeded maximum of 60 hours';
      }
      if (isWeeklyHighlightThreshold()) {
        return 'Weekly hours exceed 40 hours';
      }
      return '';
    }

    const dayHours = getDayHours(day);
    const maxHours = getShiftMaxHours();
    
    if (isDailyMaxExceeded(day)) {
      return 'Daily hours exceeded maximum of 12 hours';
    }
    
    if (isDayHoursExceeded(day)) {
      const currentShift = data.shift?.label || parentData?.shift?.label || 'shift';
      return `Daily hours exceeded ${currentShift} maximum of ${maxHours} hours`;
    }
    
    return '';
  };

  const shouldHighlightCell = (day: string): boolean => {
    if (day === 'total') {
      return isWeeklyMaxExceeded() || isWeeklyHighlightThreshold();
    }
    return isDailyMaxExceeded(day) || isDayHoursExceeded(day);
  };

  // Handle lock/unlock toggle
  const handleLockToggle = () => {
    const newLockState = !isShiftLocked;
    setIsShiftLocked(newLockState);
    onUpdate(data.id, { isLocked: newLockState });
  };

  // Handle delete
  const handleDelete = () => {
    if (onDelete) {
      onDelete(data.id);
    }
  };

  // Get shift display value - non-editable showing parent's shift
  const getShiftDisplayValue = () => {
    if (data.shift) {
      return data.shift.label;
    }
    return '';
  };

  // Check if day is disabled based on dayFilter and inputsEnabled
  const isDayDisabled = (dayName: string): boolean => {
    const dayItem = dayFilter.find(d => d.name.toLowerCase() === dayName.toLowerCase());
    const dayFilterDisabled = dayItem ? !dayItem.enabled : false;
    return dayFilterDisabled || !inputsEnabled;
  };

  // Check if day is selected
  const isDaySelected = (dayKey: string): boolean => {
    return selectedDays.includes(dayKey);
  };

  // Get cell style based on day state
  const getDayCellStyle = (dayName: string, dayKey: string) => {
    const isDisabled = isDayDisabled(dayName);
    const isSelected = isDaySelected(dayKey);
    
    if (isDisabled) {
      return [styles.dayCell, styles.disabledDayCell];
    }
    if (isSelected) {
      return [styles.dayCell, styles.selectedDayCell];
    }
    return styles.dayCell;
  };

  // Lock Icon SVG
  const LockIcon = () => (
    <svg width="13" height="15" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.625 5H8.9375V3.57143C8.9375 2.97619 8.70052 2.47024 8.22656 2.05357C7.7526 1.63691 7.17708 1.42857 6.5 1.42857C5.82292 1.42857 5.2474 1.63691 4.77344 2.05357C4.29948 2.47024 4.0625 2.97619 4.0625 3.57143H2.4375C2.4375 2.58333 2.83373 1.74119 3.62619 1.045C4.41865 0.34881 5.37658 0.000476678 6.5 4.87567e-07C7.62342 -0.000475703 8.58162 0.347858 9.37462 1.045C10.1676 1.74214 10.5636 2.58429 10.5625 3.57143V5H11.375C11.8219 5 12.2046 5.14 12.5231 5.42C12.8416 5.7 13.0005 6.03619 13 6.42857V13.5714C13 13.9643 12.841 14.3007 12.5231 14.5807C12.2051 14.8607 11.8224 15.0005 11.375 15H1.625C1.17812 15 0.795708 14.8602 0.47775 14.5807C0.159792 14.3012 0.000541667 13.9648 0 13.5714V6.42857C0 6.03571 0.15925 5.69952 0.47775 5.42C0.79625 5.14048 1.17867 5.00048 1.625 5ZM1.625 13.5714H11.375V6.42857H1.625V13.5714ZM6.5 11.4286C6.94687 11.4286 7.32956 11.2888 7.64806 11.0093C7.96656 10.7298 8.12554 10.3933 8.125 10C8.12446 9.60667 7.96548 9.27048 7.64806 8.99143C7.33065 8.71238 6.94796 8.57238 6.5 8.57143C6.05204 8.57048 5.66962 8.71048 5.35275 8.99143C5.03587 9.27238 4.87662 9.60857 4.875 10C4.87337 10.3914 5.03262 10.7279 5.35275 11.0093C5.67287 11.2907 6.05529 11.4305 6.5 11.4286Z" fill="#3A3A3A"/>
    </svg>
  );

  // Unlock Icon SVG
  const UnlockIcon = () => (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.58832 13.5714H11.1182V6.42857H1.58832V13.5714ZM6.35327 11.4286C6.79005 11.4286 7.1641 11.2888 7.47541 11.0093C7.78672 10.7298 7.94211 10.3933 7.94158 10C7.94105 9.60667 7.78566 9.27048 7.47541 8.99143C7.16516 8.71238 6.79111 8.57238 6.35327 8.57143C5.91542 8.57048 5.54164 8.71048 5.23191 8.99143C4.92219 9.27238 4.76654 9.60857 4.76495 10C4.76336 10.3914 4.91902 10.7279 5.23191 11.0093C5.54481 11.2907 5.9186 11.4305 6.35327 11.4286ZM1.58832 15C1.15153 15 0.777745 14.8602 0.466965 14.5807C0.156184 14.3012 0.000529439 13.9648 0 13.5714V6.42857C0 6.03571 0.155655 5.69952 0.466965 5.42C0.778275 5.14048 1.15206 5.00048 1.58832 5H7.14742V3.57143C7.14742 2.58333 7.53471 1.74119 8.30928 1.045C9.08385 0.34881 10.0202 0.00047619 11.1182 0C12.058 0 12.8786 0.255952 13.5801 0.767857C14.2816 1.27976 14.7449 1.92262 14.9699 2.69643C15.0361 2.89881 14.9932 3.09524 14.8412 3.28571C14.6893 3.47619 14.5072 3.57143 14.2948 3.57143C14.0698 3.57143 13.8845 3.52976 13.7389 3.44643C13.5933 3.36309 13.4875 3.22619 13.4213 3.03571C13.2757 2.58333 12.9945 2.20238 12.5779 1.89286C12.1612 1.58333 11.6747 1.42857 11.1182 1.42857C10.4564 1.42857 9.89389 1.6369 9.43063 2.05357C8.96737 2.47024 8.73574 2.97619 8.73574 3.57143V5H11.1182C11.555 5 11.9291 5.14 12.2404 5.42C12.5517 5.7 12.7071 6.03619 12.7065 6.42857V13.5714C12.7065 13.9643 12.5511 14.3007 12.2404 14.5807C11.9296 14.8607 11.5555 15.0005 11.1182 15H1.58832Z" fill="#3A3A3A"/>
    </svg>
  );

  // Delete Icon SVG
  const DeleteIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 6.75V14.25H6V6.75H12ZM10.875 2.25H7.125L6.375 3H3.75V4.5H14.25V3H11.625L10.875 2.25ZM13.5 5.25H4.5V14.25C4.5 15.075 5.175 15.75 6 15.75H12C12.825 15.75 13.5 15.075 13.5 14.25V5.25Z" fill="#3A3A3A"/>
    </svg>
  );

  return (
    <View style={styles.container}>
      {/* Checkbox Column */}
      <View style={styles.checkboxCell}>
        <TouchableOpacity
          style={[
            styles.checkboxContainer,
            shouldDisableCheckbox && styles.disabledCheckboxContainer
          ]}
          onPress={handleCheckboxChange}
          activeOpacity={shouldDisableCheckbox ? 1 : 0.8}
          disabled={shouldDisableCheckbox}
        >
          <View style={[
            styles.checkbox,
            data.isSelected && styles.checkboxSelected,
            shouldDisableCheckbox && styles.disabledCheckbox
          ]}>
            {data.isSelected && <Text style={styles.checkboxCheck}>✓</Text>}
          </View>
        </TouchableOpacity>
      </View>

      {/* Actions Column with Lock and Delete Icons */}
      <View style={styles.actionsCell}>
        <View style={styles.actionsContainer}>
          <TouchableOpacity onPress={handleLockToggle} style={styles.iconButton}>
            {isShiftLocked ? <LockIcon /> : <UnlockIcon />}
          </TouchableOpacity>
          <TouchableOpacity onPress={handleDelete} style={styles.iconButton}>
            <DeleteIcon />
          </TouchableOpacity>
        </View>
      </View>

      {/* Role Column */}
      <View style={styles.roleCell}>
        <DropDownPicker
          open={roleDropdownOpen}
          value={roleValue}
          items={roleOptions.map(option => ({
            label: option.label,
            value: option.value
          }))}
          setOpen={setRoleDropdownOpen}
          setValue={setRoleValue}
          onSelectItem={(item) => handleRoleSelect(item.value || null)}
          placeholder="Select Role"
          disabled={isRoleLoading || !inputsEnabled}
          style={[styles.dropdown, !inputsEnabled && styles.dropdownStyleDisabled, data.isRoleInvalid && styles.dropdownStyleError]}
          dropDownContainerStyle={styles.dropdownContainer}
          textStyle={styles.dropdownText}
          placeholderStyle={styles.dropdownPlaceholder}
          zIndex={1000}
          zIndexInverse={3000}
          dropDownDirection='TOP'
        />
      </View>

      {/* Shift Column - Editable when unlocked, read-only when locked */}
      <View style={styles.shiftCell}>
        {isShiftLocked || !inputsEnabled ? (
          <View style={styles.shiftDisplayContainer}>
            <Text style={styles.shiftDisplayText}>{getShiftDisplayValue()}</Text>
          </View>
        ) : (
          <DropDownPicker
            open={shiftDropdownOpen}
            value={shiftValue}
            items={shiftOptions.map(option => ({
              label: option.label,
              value: option.value
            }))}
            setOpen={setShiftDropdownOpen}
            setValue={setShiftValue}
            onSelectItem={(item) => handleShiftSelect(item.value || null)}
            placeholder="Select Shift"
            disabled={isShiftLoading || !inputsEnabled}
            style={[styles.dropdown, !inputsEnabled && styles.dropdownStyleDisabled]}
            dropDownContainerStyle={styles.dropdownContainer}
            textStyle={styles.dropdownText}
            placeholderStyle={styles.dropdownPlaceholder}
            zIndex={999}
            zIndexInverse={3001}
            dropDownDirection='TOP'
          />
        )}
      </View>

      {/* Day Columns - Editable Input Fields */}
      <View style={getDayCellStyle('Sunday', 'sun')}>
        <TextInput
          style={[
            styles.hourInput, 
            isDayDisabled('Sunday') && styles.disabledHourInput,
            shouldHighlightCell('sunday') && styles.validationErrorInput
          ]}
          value={data.sunday}
          onChangeText={!isDayDisabled('Sunday') ? (value) => handleHourChange('sunday', value) : undefined}
          keyboardType="numeric"
          editable={!isDayDisabled('Sunday')}
          {...(Platform.OS === 'web' && {
            title: shouldHighlightCell('sunday') ? getValidationMessage('sunday') : undefined
          })}
        />
        {shouldHighlightCell('sunday') && Platform.OS !== 'web' && (
          <TouchableOpacity
            style={styles.validationIcon}
            onPress={() => alert(getValidationMessage('sunday'))}
          >
            <Text style={styles.validationIconText}>!</Text>
          </TouchableOpacity>
        )}
      </View>
      <View style={getDayCellStyle('Monday', 'mon')}>
        <TextInput
          style={[
            styles.hourInput, 
            isDayDisabled('Monday') && styles.disabledHourInput,
            shouldHighlightCell('monday') && styles.validationErrorInput
          ]}
          value={data.monday}
          onChangeText={!isDayDisabled('Monday') ? (value) => handleHourChange('monday', value) : undefined}
          keyboardType="numeric"
          editable={!isDayDisabled('Monday')}
          {...(Platform.OS === 'web' && {
            title: shouldHighlightCell('monday') ? getValidationMessage('monday') : undefined
          })}
        />
        {shouldHighlightCell('monday') && Platform.OS !== 'web' && (
          <TouchableOpacity
            style={styles.validationIcon}
            onPress={() => alert(getValidationMessage('monday'))}
          >
            <Text style={styles.validationIconText}>!</Text>
          </TouchableOpacity>
        )}
      </View>
      <View style={getDayCellStyle('Tuesday', 'tue')}>
        <TextInput
          style={[
            styles.hourInput, 
            isDayDisabled('Tuesday') && styles.disabledHourInput,
            shouldHighlightCell('tuesday') && styles.validationErrorInput
          ]}
          value={data.tuesday}
          onChangeText={!isDayDisabled('Tuesday') ? (value) => handleHourChange('tuesday', value) : undefined}
          keyboardType="numeric"
          editable={!isDayDisabled('Tuesday')}
          {...(Platform.OS === 'web' && {
            title: shouldHighlightCell('tuesday') ? getValidationMessage('tuesday') : undefined
          })}
        />
        {shouldHighlightCell('tuesday') && Platform.OS !== 'web' && (
          <TouchableOpacity
            style={styles.validationIcon}
            onPress={() => alert(getValidationMessage('tuesday'))}
          >
            <Text style={styles.validationIconText}>!</Text>
          </TouchableOpacity>
        )}
      </View>
      <View style={getDayCellStyle('Wednesday', 'wed')}>
        <TextInput
          style={[
            styles.hourInput, 
            isDayDisabled('Wednesday') && styles.disabledHourInput,
            shouldHighlightCell('wednesday') && styles.validationErrorInput
          ]}
          value={data.wednesday}
          onChangeText={!isDayDisabled('Wednesday') ? (value) => handleHourChange('wednesday', value) : undefined}
          keyboardType="numeric"
          editable={!isDayDisabled('Wednesday')}
          {...(Platform.OS === 'web' && {
            title: shouldHighlightCell('wednesday') ? getValidationMessage('wednesday') : undefined
          })}
        />
        {shouldHighlightCell('wednesday') && Platform.OS !== 'web' && (
          <TouchableOpacity
            style={styles.validationIcon}
            onPress={() => alert(getValidationMessage('wednesday'))}
          >
            <Text style={styles.validationIconText}>!</Text>
          </TouchableOpacity>
        )}
      </View>
      <View style={getDayCellStyle('Thursday', 'thu')}>
        <TextInput
          style={[
            styles.hourInput, 
            isDayDisabled('Thursday') && styles.disabledHourInput,
            shouldHighlightCell('thursday') && styles.validationErrorInput
          ]}
          value={data.thursday}
          onChangeText={!isDayDisabled('Thursday') ? (value) => handleHourChange('thursday', value) : undefined}
          keyboardType="numeric"
          editable={!isDayDisabled('Thursday')}
          {...(Platform.OS === 'web' && {
            title: shouldHighlightCell('thursday') ? getValidationMessage('thursday') : undefined
          })}
        />
        {shouldHighlightCell('thursday') && Platform.OS !== 'web' && (
          <TouchableOpacity
            style={styles.validationIcon}
            onPress={() => alert(getValidationMessage('thursday'))}
          >
            <Text style={styles.validationIconText}>!</Text>
          </TouchableOpacity>
        )}
      </View>
      <View style={getDayCellStyle('Friday', 'fri')}>
        <TextInput
          style={[
            styles.hourInput, 
            isDayDisabled('Friday') && styles.disabledHourInput,
            shouldHighlightCell('friday') && styles.validationErrorInput
          ]}
          value={data.friday}
          onChangeText={!isDayDisabled('Friday') ? (value) => handleHourChange('friday', value) : undefined}
          keyboardType="numeric"
          editable={!isDayDisabled('Friday')}
          {...(Platform.OS === 'web' && {
            title: shouldHighlightCell('friday') ? getValidationMessage('friday') : undefined
          })}
        />
        {shouldHighlightCell('friday') && Platform.OS !== 'web' && (
          <TouchableOpacity
            style={styles.validationIcon}
            onPress={() => alert(getValidationMessage('friday'))}
          >
            <Text style={styles.validationIconText}>!</Text>
          </TouchableOpacity>
        )}
      </View>
      <View style={getDayCellStyle('Saturday', 'sat')}>
        <TextInput
          style={[
            styles.hourInput, 
            isDayDisabled('Saturday') && styles.disabledHourInput,
            shouldHighlightCell('saturday') && styles.validationErrorInput
          ]}
          value={data.saturday}
          onChangeText={!isDayDisabled('Saturday') ? (value) => handleHourChange('saturday', value) : undefined}
          keyboardType="numeric"
          editable={!isDayDisabled('Saturday')}
          {...(Platform.OS === 'web' && {
            title: shouldHighlightCell('saturday') ? getValidationMessage('saturday') : undefined
          })}
        />
        {shouldHighlightCell('saturday') && Platform.OS !== 'web' && (
          <TouchableOpacity
            style={styles.validationIcon}
            onPress={() => alert(getValidationMessage('saturday'))}
          >
            <Text style={styles.validationIconText}>!</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Total Column */}
      <View style={styles.totalCell}>
        <TextInput
          style={[
            styles.totalInput,
            !inputsEnabled && styles.disabledTotalInput,
            shouldHighlightCell('total') && styles.validationErrorInput
          ]}
          value={data.total || '0'}
          editable={false}
          textAlign="center"
          {...(Platform.OS === 'web' && {
            title: shouldHighlightCell('total') ? getValidationMessage('total') : undefined
          })}
        />
        {shouldHighlightCell('total') && Platform.OS !== 'web' && (
          <TouchableOpacity
            style={styles.validationIcon}
            onPress={() => alert(getValidationMessage('total'))}
          >
            <Text style={styles.validationIconText}>!</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 1416,
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9F9F9',
    borderBottomWidth: 1,
    borderBottomColor: '#DFDFDF',
  },
  checkboxCell: {
    width: 45,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    // borderRightWidth: 1,
    borderRightColor: '#DFDFDF',
  },
  checkboxContainer: {
    padding: 4,
  },
  disabledCheckboxContainer: {
    opacity: 0.5,
  },
  checkbox: {
    width: 12,
    height: 12,
    borderWidth: 1,
    borderColor: '#DFDFDF',
    borderRadius: 3,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  disabledCheckbox: {
    backgroundColor: '#F5F5F5',
    borderColor: '#C0C0C0',
  },
  checkboxSelected: {
    backgroundColor: '#005CBC',
    borderColor: '#005CBC',
  },
  checkboxCheck: {
    color: '#FFFFFF',
    fontSize: 8,
    fontWeight: 'bold',
  },
  actionsCell: {
    width: 240,
    height: 40,
    justifyContent: 'center',
    alignItems: 'flex-end',
    // borderRightWidth: 1,
    borderRightColor: '#DFDFDF',
    paddingHorizontal: 8,
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  iconButton: {
    padding: 4,
    borderRadius: 4,
  },
  roleCell: {
    width: 165,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    // borderRightWidth: 1,
    borderRightColor: '#DFDFDF',
    paddingHorizontal: 8,
  },
  shiftCell: {
    width: 165,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    // borderRightWidth: 1,
    borderRightColor: '#DFDFDF',
    paddingHorizontal: 8,
  },
  shiftDisplayContainer: {
    width: '100%',
    height: 28,
    backgroundColor: '#F8F9FA',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#DFDFDF',
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
  shiftDisplayText: {
    fontSize: 12,
    color: '#666',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  dayCell: {
    width: 93,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    // borderRightWidth: 1,
    borderRightColor: '#DFDFDF',
  },
  selectedDayCell: {
    backgroundColor: '#E3F2FD',
  },
  disabledDayCell: {
    backgroundColor: '#F5F5F5',
  },
  dayText: {
    fontSize: 14,
    color: '#333',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  hourInput: {
    height: 24,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    backgroundColor: '#FBFCFE',
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    paddingHorizontal: 8,
    minHeight: 24,
    textAlign: 'center',
    width: 76,
  },
  disabledHourInput: {
    backgroundColor: '#F5F5F5',
    borderColor: '#D3D3D3',
    color: '#999999',
    opacity: 0.8,
    width: 76, // Ensure width is maintained when disabled
  },
  validationErrorInput: {
    borderColor: '#FFD700', // Yellow border for validation errors
    borderWidth: 2,
    backgroundColor: '#FFFACD', // Light yellow background
  },
  validationIcon: {
    position: 'absolute',
    right: 2,
    top: 2,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#FFD700',
    justifyContent: 'center',
    alignItems: 'center',
  },
  validationIconText: {
    color: '#B8860B',
    fontSize: 10,
    fontWeight: 'bold',
  },
  totalCell: {
    width: 204,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    // borderRightWidth: 1,
    borderRightColor: '#DFDFDF',
    paddingHorizontal: 10,
  },
  totalInput: {
    height: 24,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    backgroundColor: '#F5F5F5',
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    lineHeight: 18,
    paddingHorizontal: 8,
    minHeight: 24,
    textAlign: 'center',
    width: 76,
    marginRight: 50, // Add right margin for Total Hours Scheduled column consistency
  },
  disabledTotalInput: {
    backgroundColor: '#F5F5F5',
    borderColor: '#D3D3D3',
    color: '#3A3A3A', // Always rgb(58, 58, 58)
    opacity: 0.8,
    width: 76, // Ensure width is maintained when disabled
  },
  dropdown: {
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#C0C0C0',
    borderRadius: 4,
    minHeight: 24,
    height: 24,
  },
  dropdownStyleDisabled: {
    backgroundColor: '#F5F5F5',
    borderColor: '#D3D3D3',
    opacity: 0.7,
  },
  dropdownStyleError: {
    borderColor: '#FF4444',
    borderWidth: 2,
  },
  dropdownContainer: {
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#C0C0C0',
    borderRadius: 4,
    marginTop: 2,
  },
  dropdownText: {
    fontSize: 12,
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  dropdownPlaceholder: {
    fontSize: 12,
    color: '#666',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
});

export default DLOCreateScheduleSplitRoleRow;